'use client'

import { useEffect, useId, useRef, useState } from 'react'
import { AlertCircle, Bug, X, CheckCircle2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

type BugReportModalProps = {
  open: boolean
  onClose: () => void
}

const initialForm = {
  email: '',
  provider: '',
  os: '',
  version: '',
  setup: '',
  description: '',
  website: '',
}

export function BugReportModal({ open, onClose }: BugReportModalProps) {
  const [form, setForm] = useState(initialForm)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const titleId = useId()
  const firstFieldRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    document.body.style.overflow = 'hidden'
    // focus the first field when the modal opens
    const t = window.setTimeout(() => firstFieldRef.current?.focus(), 50)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = ''
      window.clearTimeout(t)
    }
  }, [open, onClose])

  // Reset back to a clean form shortly after the modal closes.
  useEffect(() => {
    if (open) return
    const t = window.setTimeout(() => {
      setForm(initialForm)
      setSubmitted(false)
      setSubmitting(false)
      setError('')
    }, 200)
    return () => window.clearTimeout(t)
  }, [open])

  if (!open) return null

  const update =
    (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const res = await fetch('/api/bug-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      const data = (await res.json().catch(() => ({}))) as { error?: string }
      if (!res.ok) throw new Error(data.error || 'Could not send the report.')
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the report.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        aria-label="Close bug report form"
        onClick={onClose}
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
      />

      <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-border/60 p-6">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-primary">
              <Bug className="h-5 w-5" />
            </span>
            <div>
              <h2 id={titleId} className="text-lg font-semibold">
                Report a bug
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                A focused report so we can reproduce and fix the issue fast.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {submitted ? (
          <div className="flex flex-col items-center gap-4 p-8 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary">
              <CheckCircle2 className="h-6 w-6" />
            </span>
            <div>
              <h3 className="text-base font-semibold">Report sent</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                Thanks for taking the time to report this. Your message was sent to the NewEmail
                team.
              </p>
            </div>
            <Button variant="outline" onClick={onClose}>
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
            <div className="sr-only" aria-hidden="true">
              <label htmlFor="br-website">Website</label>
              <input
                id="br-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={update('website')}
              />
            </div>

            <Field label="Your email" htmlFor="br-email">
              <input
                ref={firstFieldRef}
                id="br-email"
                type="email"
                required
                maxLength={254}
                value={form.email}
                onChange={update('email')}
                placeholder="you@example.com"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30"
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Mail provider" htmlFor="br-provider">
                <input
                  id="br-provider"
                  type="text"
                  required
                  maxLength={120}
                  value={form.provider}
                  onChange={update('provider')}
                  placeholder="iCloud, Gmail, or a custom domain"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30"
                />
              </Field>
              <Field label="Operating system" htmlFor="br-os">
                <input
                  id="br-os"
                  type="text"
                  required
                  maxLength={80}
                  value={form.os}
                  onChange={update('os')}
                  placeholder="macOS 15"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30"
                />
              </Field>
              <Field label="NewEmail version" htmlFor="br-version">
                <input
                  id="br-version"
                  type="text"
                  maxLength={40}
                  value={form.version}
                  onChange={update('version')}
                  placeholder="1.0.4"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30"
                />
              </Field>
              <Field label="Account setup" htmlFor="br-setup">
                <input
                  id="br-setup"
                  type="text"
                  maxLength={120}
                  value={form.setup}
                  onChange={update('setup')}
                  placeholder="IMAP + SMTP, one account"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30"
                />
              </Field>
            </div>

            <Field label="What happened & what you expected" htmlFor="br-description">
              <textarea
                id="br-description"
                required
                rows={4}
                maxLength={5000}
                value={form.description}
                onChange={update('description')}
                placeholder="Describe the steps, the result, and any error messages copied exactly."
                className="w-full resize-y rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30"
              />
            </Field>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="mt-2 flex items-center justify-end gap-3">
              <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Bug className="h-4 w-4" />
                )}
                Send report
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <label htmlFor={htmlFor} className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      {children}
    </label>
  )
}
